import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config.json';

const SettingsContext = createContext();

export function SettingsProvider({ children }) {
  const { i18n } = useTranslation();

  const defaultSettings = {
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12hr',
    theme: 'Light',
    colorTheme: '#20A8E0',
    mapview: 'Roadmap',
    fontStyle: 'Roboto',
    mapLayers: 'Traffic',
    defaultLanguage: 'en',
  };

  const [settings, setSettings] = useState(() => {
    // Load settings from localStorage on mount to handle refresh
    const savedSettings = localStorage.getItem('appSettings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(0);

  const fetchSettings = useCallback(
    async (organizationId) => {
      if (!organizationId) {
        console.warn('No organizationId provided, using default/local settings');
        setSettings((prev) => ({ ...defaultSettings, ...prev }));
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `${config.baseURL}/appSettings/organization?organizationId=${organizationId}`,
          { withCredentials: true }
        );

        const fetchedSettings = {
          ...defaultSettings,
          ...response.data,
          defaultLanguage:
            response.data.defaultLanguage?.toLowerCase() ||
            localStorage.getItem('i18nextLng') ||
            defaultSettings.defaultLanguage,
        };

        // console.log('Fetched settings:', fetchedSettings);
        setSettings(fetchedSettings);
        // Save to localStorage for persistence
        localStorage.setItem('appSettings', JSON.stringify(fetchedSettings));
        // Apply language
        i18n.changeLanguage(fetchedSettings.defaultLanguage);
        localStorage.setItem('i18nextLng', fetchedSettings.defaultLanguage);
      } catch (error) {
        console.error('Error fetching settings:', error);
        const localLanguage = localStorage.getItem('i18nextLng') || defaultSettings.defaultLanguage;
        const fallbackSettings = {
          ...defaultSettings,
          defaultLanguage: localLanguage,
        };
        setSettings(fallbackSettings);
        localStorage.setItem('appSettings', JSON.stringify(fallbackSettings));
        i18n.changeLanguage(localLanguage);
      } finally {
        setLoading(false);
      }
    },
    [i18n]
  );

  const updateSettings = (newSettings) => {
    const updatedSettings = {
      ...settings,
      ...newSettings,
      defaultLanguage:
        newSettings.defaultLanguage?.toLowerCase() || settings.defaultLanguage,
    };
    console.log('Updating settings:', updatedSettings);
    setSettings(updatedSettings);
    setLastUpdate(Date.now());
    localStorage.setItem('appSettings', JSON.stringify(updatedSettings)); // Persist to localStorage
    i18n.changeLanguage(updatedSettings.defaultLanguage);
    localStorage.setItem('i18nextLng', updatedSettings.defaultLanguage);
  };

  const applyTheme = (theme, colorTheme, fontStyle, language) => {
    document.documentElement.setAttribute('data-theme', theme.toLowerCase());
    document.documentElement.style.setProperty('--primary-color', colorTheme);
    document.documentElement.style.setProperty('--font-family', fontStyle);
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  };

  useEffect(() => {
    if (settings && !loading && settings.defaultLanguage) {
      // console.log('Applying settings:', settings);
      applyTheme(
        settings.theme,
        settings.colorTheme,
        settings.fontStyle,
        settings.defaultLanguage
      );
    }
  }, [settings, loading]);

  // Fetch settings on mount if organizationId exists (e.g., after login)
  useEffect(() => {
    const organizationId = localStorage.getItem('organizationId'); // Assuming stored after login
    if (organizationId) {
      fetchSettings(organizationId);
    } else {
      setLoading(false); // Use defaults if no organizationId
    }
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, updateSettings, fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}